import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import {
  BanknotesIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  TagIcon,
  DocumentIcon,
  DocumentArrowDownIcon,
  ArrowsRightLeftIcon,
  CheckIcon,
  XMarkIcon,
  ListBulletIcon,
  ChevronRightIcon as ChevronRightIconSolid,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import StatementDetailModal from '../components/modals/StatementDetailModal';
import ConfirmationModal from '../components/ConfirmationModal';
import ExcelExportModal from '../components/ExcelExportModal';
import CSVImporter from '../components/CSVImporter';
import AddBookingModal from '../components/AddBookingModal';

// === Story-Hero-Card Constants ===
const CARD_BG = '#1c2432';

interface Account {
  id: number | string;
  name: string;
  kontotyp?: string;
  startbetrag?: number;
  currentBalance?: number;
  erstellt_am?: string;
}

interface Statement {
  id: string;
  originalId: string;
  buchungstag: string;
  buchungstext: string;
  verwendungszweck: string;
  betrag: number;
  saldo: number | null;
  konto_id: number;
  von_konto_id?: number;
  nach_konto_id?: number;
  kontotyp: string;
  documents: any[];
  categories: any[];
  tags?: string[];
  name_zahlungsbeteiligter?: string;
  cumulativeBalance?: number;
}

const AccountStatements: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [selectedStatementId, setSelectedStatementId] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [allStatements, setAllStatements] = useState<Statement[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const statementsPerPage = 20;
  const [statusFilter, setStatusFilter] = useState<'all' | 'red' | 'blue' | 'green'>('all');
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [availableBuchungTags, setAvailableBuchungTags] = useState<{tag: string; count: number}[]>([]);

  // Selection mode states
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkCategoryDropdown, setShowBulkCategoryDropdown] = useState(false);
  const [categoryMasters, setCategoryMasters] = useState<any[]>([]);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const bulkCategoryRef = useRef<HTMLDivElement>(null);

  // Modal states
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);

  // ConfirmationModal State
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    type: 'info' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
  });

  // Fetch all data once
  const fetchData = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      // 1. Fetch accounts
      const resAccounts = await apiClient.get('/api/finance/accounts');
      const accountsData = resAccounts.data || [];
      const enrichedAccounts = [{ id: 'all', name: 'Alle Konten' }, ...accountsData];
      setAccounts(enrichedAccounts);

      // 2. Fetch buchung tags
      apiClient.get('/api/finance/buchung-tags').then(res => {
        setAvailableBuchungTags(res.data || []);
      }).catch(() => {});

      // 3. Fetch all statements
      const resStatements = await apiClient.get(`/api/finance/statements-list?account_id=all&limit=0&offset=0`);

      if (resStatements.data.success) {
        const statementsData = resStatements.data.statements || [];

        const allStatementsData: Statement[] = statementsData.map((stmt: any) => ({
          ...stmt,
          originalId: stmt.id,
          id: `${stmt.konto_id}-${stmt.id}`,
          konto_id: Number(stmt.konto_id),
          saldo: stmt.saldo != null ? Number(stmt.saldo) : null,
          von_konto_id: stmt.von_konto_id ? Number(stmt.von_konto_id) : undefined,
          nach_konto_id: stmt.nach_konto_id ? Number(stmt.nach_konto_id) : undefined,
        }));

        // Backend liefert bereits DESC sortiert
        setAllStatements(allStatementsData);
      }
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError('Fehler beim Laden der Kontoauszüge');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Selection mode: Kategorien laden
  const enterSelectionMode = useCallback(async () => {
    setSelectionMode(true);
    setSelectedIds(new Set());
    try {
      const res = await apiClient.get('/api/finance/categories');
      setCategoryMasters(res.data || []);
    } catch (err) {
      console.error('Kategorien laden fehlgeschlagen:', err);
    }
  }, []);

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
    setShowBulkCategoryDropdown(false);
  }, []);

  const toggleSelection = useCallback((originalId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(originalId)) {
        next.delete(originalId);
      } else {
        next.add(originalId);
      }
      return next;
    });
  }, []);

  const sortedBulkCategories = useMemo(() => {
    return [...categoryMasters].sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '', 'de'));
  }, [categoryMasters]);

  // Keyboard navigation for bulk category dropdown
  useEffect(() => {
    if (!showBulkCategoryDropdown) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (key.length !== 1 || !key.match(/[a-zäöüß]/)) return;
      const container = bulkCategoryRef.current;
      if (!container) return;
      const match = sortedBulkCategories.findIndex((cat: any) => (cat.name || '').toLowerCase().startsWith(key));
      if (match >= 0) {
        const el = container.children[match] as HTMLElement;
        el?.scrollIntoView({ block: 'nearest' });
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showBulkCategoryDropdown, sortedBulkCategories]);

  const handleBulkCategoryAssign = useCallback(async (kategorieId: number) => {
    if (selectedIds.size === 0) return;
    setBulkAssigning(true);
    setShowBulkCategoryDropdown(false);
    try {
      await apiClient.post('/api/finance/statements/bulk-categories', {
        statement_ids: Array.from(selectedIds),
        kategorie_id: kategorieId
      });
      await fetchData();
      exitSelectionMode();
    } catch (err) {
      console.error('Bulk-Kategorie-Zuweisung fehlgeschlagen:', err);
    } finally {
      setBulkAssigning(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, exitSelectionMode]);

  // Betrag und Saldo sind bereits korrekt aus der DB (Umbuchungen haben 2 Zeilen mit korrektem Vorzeichen)
  const accountAdjustedStatements = allStatements;

  // Available years from loaded statements
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    allStatements.forEach((stmt) => {
      if (stmt && stmt.buchungstag) {
        const year = new Date(stmt.buchungstag).getFullYear();
        if (!isNaN(year) && year >= 1900 && year <= 2100) {
          years.add(year);
        }
      }
    });
    return ['all', ...Array.from(years).sort((a, b) => b - a)];
  }, [allStatements]);

  // Filter statements client-side
  const filteredStatements = useMemo(() => {
    if (!Array.isArray(accountAdjustedStatements)) return [];

    let filtered = accountAdjustedStatements.filter((stmt) => {
      if (!stmt) return false;

      // Account filter
      if (selectedAccount !== 'all') {
        const accId = parseInt(selectedAccount);
        if (stmt.konto_id !== accId) return false;
      }

      // Year filter
      if (selectedYear !== 'all') {
        const stmtYear = new Date(stmt.buchungstag).getFullYear();
        if (stmtYear !== parseInt(selectedYear)) return false;
      }

      // Status filter
      if (statusFilter !== 'all' && stmt.originalId !== 'start' && !(stmt.von_konto_id && stmt.nach_konto_id)) {
        const categorizedAmount = (stmt.categories || []).reduce((sum: number, cat: any) => sum + (cat.betrag || 0), 0);
        const documentAmount = (stmt.documents || []).reduce((sum: number, doc: any) => sum + (doc.invoice_amount || 0), 0);
        const hasCategories = stmt.categories && stmt.categories.length > 0;
        const hasDocuments = stmt.documents && stmt.documents.length > 0;
        const categoryMatches = hasCategories && Math.abs(categorizedAmount - Math.abs(stmt.betrag)) < 0.01;
        const documentMatches = hasDocuments && documentAmount > 0 && Math.abs(documentAmount - Math.abs(stmt.betrag)) < 0.01;

        let stmtStatusColor: 'red' | 'blue' | 'green' = 'green';
        if (!hasCategories || (!hasDocuments && stmt.betrag < 0)) {
          stmtStatusColor = 'red';
        } else if (!categoryMatches || (hasDocuments && !documentMatches)) {
          stmtStatusColor = 'blue';
        } else if (categoryMatches && documentMatches) {
          stmtStatusColor = 'green';
        }

        if (statusFilter !== stmtStatusColor) {
          return false;
        }
      }

      // Tag filter
      if (filterTags.length > 0) {
        const stmtTags = stmt.tags || [];
        if (!filterTags.every(ft => stmtTags.includes(ft))) return false;
      }

      // Client-side search filter (instant)
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesSearch =
          (stmt.buchungstext || '').toLowerCase().includes(term) ||
          (stmt.verwendungszweck || '').toLowerCase().includes(term) ||
          (stmt.name_zahlungsbeteiligter || '').toLowerCase().includes(term) ||
          (stmt.tags || []).some(t => t.toLowerCase().includes(term));
        if (!matchesSearch) return false;
      }

      return true;
    });

    return filtered;
  }, [accountAdjustedStatements, selectedAccount, selectedYear, statusFilter, searchTerm, filterTags]);

  // Nur bei Filter-Änderungen auf Seite 1 zurücksetzen, nicht bei Daten-Updates
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedAccount, selectedYear, statusFilter, searchTerm, filterTags]);

  // Selection helpers (brauchen filteredStatements)
  const selectAllVisible = useCallback(() => {
    const ids = new Set<string>();
    filteredStatements.forEach(s => {
      if (s.originalId !== 'start' && !(s.von_konto_id && s.nach_konto_id) && (!s.categories || s.categories.length === 0)) {
        ids.add(s.originalId);
      }
    });
    setSelectedIds(ids);
  }, [filteredStatements]);

  const selectUncategorized = useCallback(() => {
    const ids = new Set<string>();
    filteredStatements.forEach(s => {
      if (s.originalId !== 'start' && !(s.von_konto_id && s.nach_konto_id) && (!s.categories || s.categories.length === 0)) {
        ids.add(s.originalId);
      }
    });
    setSelectedIds(ids);
  }, [filteredStatements]);

  const selectedSum = useMemo(() => {
    return filteredStatements
      .filter(s => selectedIds.has(s.originalId))
      .reduce((sum, s) => sum + s.betrag, 0);
  }, [selectedIds, filteredStatements]);

  // Saldo kommt direkt aus der Datenbank (pro Buchung korrekt berechnet)
  const getSaldo = (statement: Statement) => {
    return statement.saldo ?? 0;
  };

  // Pagination
  const totalPages = Math.ceil(filteredStatements.length / statementsPerPage);
  const startIndex = (currentPage - 1) * statementsPerPage;
  const paginatedStatements = filteredStatements.slice(startIndex, startIndex + statementsPerPage);

  // Group paginated statements by date for grouped rendering
  const groupedStatements = useMemo(() => {
    const groups: { date: string; statements: Statement[] }[] = [];
    const dateMap = new Map<string, Statement[]>();
    paginatedStatements.forEach(stmt => {
      const dateKey = stmt.buchungstag ? stmt.buchungstag.split('T')[0] : 'unknown';
      if (!dateMap.has(dateKey)) {
        const stmts: Statement[] = [];
        dateMap.set(dateKey, stmts);
        groups.push({ date: dateKey, statements: stmts });
      }
      dateMap.get(dateKey)!.push(stmt);
    });
    return groups;
  }, [paginatedStatements]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Formatting helpers
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);

  const formatDateFull = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatDateCompact = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getDayNumber = (dateStr: string) => new Date(dateStr).getDate();
  const getDayShort = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '');
  const getMonthShort = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('de-DE', { month: 'short' }).replace('.', '');

  const getStatusInfo = (statement: Statement): { color: 'red' | 'amber' | 'green' | 'gray' } => {
    if (statement.originalId === 'start' || (statement.von_konto_id && statement.nach_konto_id)) {
      return { color: 'gray' };
    }
    const categorizedAmount = (statement.categories || []).reduce((sum: number, cat: any) => sum + (cat.betrag || 0), 0);
    const documentAmount = (statement.documents || []).reduce((sum: number, doc: any) => sum + (doc.invoice_amount || 0), 0);
    const hasCategories = statement.categories && statement.categories.length > 0;
    const hasDocuments = statement.documents && statement.documents.length > 0;
    const categoryMatches = hasCategories && Math.abs(categorizedAmount - Math.abs(statement.betrag)) < 0.01;
    const documentMatches = hasDocuments && documentAmount > 0 && Math.abs(documentAmount - Math.abs(statement.betrag)) < 0.01;
    if (!hasCategories || (!hasDocuments && statement.betrag < 0)) return { color: 'red' };
    if (!categoryMatches || (hasDocuments && !documentMatches)) return { color: 'amber' };
    return { color: 'green' };
  };

  const renderStatusBadges = (statement: Statement) => {
    if (statement.originalId === 'start' || (statement.von_konto_id && statement.nach_konto_id)) return null;
    const categorizedAmount = (statement.categories || []).reduce((sum: number, cat: any) => sum + (cat.betrag || 0), 0);
    const documentAmount = (statement.documents || []).reduce((sum: number, doc: any) => sum + (doc.invoice_amount || 0), 0);
    const hasCategories = statement.categories && statement.categories.length > 0;
    const hasDocuments = statement.documents && statement.documents.length > 0;
    const categoryMatches = hasCategories && Math.abs(categorizedAmount - Math.abs(statement.betrag)) < 0.01;
    const documentMatches = hasDocuments && documentAmount > 0 && Math.abs(documentAmount - Math.abs(statement.betrag)) < 0.01;
    const hasMember = statement.categories?.some((cat: any) => cat.member_id);
    const badges: React.ReactElement[] = [];
    if (!hasCategories) {
      badges.push(<span key="cat" className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-500/30">Kategorie fehlt</span>);
    } else if (categoryMatches) {
      badges.push(<span key="cat" className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-primary-500/10 text-primary-700 dark:text-primary-300 border border-primary-500/30">100% kategorisiert</span>);
    }
    if (!hasDocuments && statement.betrag < 0) {
      badges.push(<span key="doc" className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30">Dokument fehlt</span>);
    } else if (hasDocuments && documentMatches) {
      badges.push(<span key="doc" className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-primary-500/10 text-primary-700 dark:text-primary-300 border border-primary-500/30">{statement.documents.length} {statement.documents.length === 1 ? 'Dokument' : 'Dokumente'}</span>);
    }
    if (categoryMatches && documentMatches) {
      badges.push(<span key="ok" className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">Vollständig</span>);
    }
    if (hasMember) {
      const memberName = statement.categories?.filter((cat: any) => cat.member_id && cat.member_name).map((cat: any) => cat.member_name).join(', ');
      badges.push(<span key="member" className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-white/5 text-gray-700 dark:text-slate-300 border border-white/10">{memberName || 'Mitglied'}</span>);
    }
    if (badges.length === 0) return null;
    return <div className="flex flex-wrap gap-1 mt-1">{badges}</div>;
  };


  // Handle statement update from modal
  const handleStatementUpdate = (updatedStatement: any) => {
    setAllStatements((prevStatements) =>
      prevStatements.map((stmt) =>
        stmt.id === `${updatedStatement.konto_id}-${updatedStatement.id}`
          ? {
              ...stmt,
              buchungstext: updatedStatement.buchungstext,
              verwendungszweck: updatedStatement.verwendungszweck,
              betrag: typeof updatedStatement.betrag === 'string'
                ? parseFloat(updatedStatement.betrag)
                : updatedStatement.betrag,
              documents: updatedStatement.documents || [],
              categories: updatedStatement.categories || [],
              tags: updatedStatement.tags || [],
            }
          : stmt
      )
    );
  };

  // Handle statement delete from modal
  const handleStatementDelete = (_statementId: string) => {
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Löschen bestätigen',
      message: 'Möchten Sie diesen Kontoauszug wirklich löschen?',
      variant: 'two',
      onConfirm: async () => {
        try {
          // Hier würde der API-Call zum Löschen kommen
          // await apiClient.delete(`/api/finance/statements/${statementId}`);

          setAllStatements((prevStatements) =>
            prevStatements.filter((stmt) => stmt.originalId !== _statementId)
          );

          setConfirmModal({
            visible: true,
            type: 'success',
            title: 'Erfolgreich',
            message: 'Der Kontoauszug wurde erfolgreich gelöscht.',
            variant: 'single',
            onConfirm: () => {},
          });
        } catch (error) {
          setConfirmModal({
            visible: true,
            type: 'error',
            title: 'Fehler',
            message: 'Der Kontoauszug konnte nicht gelöscht werden.',
            variant: 'single',
            onConfirm: () => {},
          });
        }
      },
    });
  };


  // Stats berechnen (basierend auf filteredStatements - berücksichtigt alle aktiven Filter)
  const stats = useMemo(() => {
    const filtered = filteredStatements;

    // Saldo: aus letzter Buchung (Statements sind DESC sortiert)
    let balance: number;
    if (selectedAccount === 'all') {
      const lastSaldoPerAccount = new Map<number, number>();
      for (const s of filtered) {
        if (!lastSaldoPerAccount.has(s.konto_id) && s.saldo != null) {
          lastSaldoPerAccount.set(s.konto_id, s.saldo);
        }
      }
      balance = Array.from(lastSaldoPerAccount.values()).reduce((sum, val) => sum + val, 0);
    } else {
      const lastStatement = filtered.find(s => s.saldo != null);
      balance = lastStatement?.saldo ?? 0;
    }

    // Einnahmen/Ausgaben: ohne Eröffnungsbeträge
    // "Alle Konten": Umbuchungen ausschließen (heben sich auf)
    // Einzelnes Konto: Umbuchungen einbeziehen (verändern den Saldo)
    const bookingsForStats = filtered.filter(s => {
      if (s.originalId === 'start') return false;
      if (selectedAccount === 'all' && s.von_konto_id && s.nach_konto_id) return false;
      return true;
    });
    const income = bookingsForStats.filter(s => s.betrag > 0).reduce((sum, s) => sum + s.betrag, 0);
    const expenses = bookingsForStats.filter(s => s.betrag < 0).reduce((sum, s) => sum + Math.abs(s.betrag), 0);
    const totalTransactions = filtered.filter(s => s.originalId !== 'start').length;
    const categorizedCount = filtered.filter(s => s.categories && s.categories.length > 0).length;
    const documentsCount = filtered.reduce((sum, s) => sum + (s.documents?.length || 0), 0);

    return { balance, income, expenses, totalTransactions, categorizedCount, documentsCount };
  }, [filteredStatements, selectedAccount]);

  // Story-Helper: Zeitraum + Buchungs-Anzahl
  const storyMeta = useMemo(() => {
    const items = filteredStatements.filter(s => s.originalId !== 'start');
    if (items.length === 0) {
      return { count: 0, fromLabel: '', toLabel: '', uncategorized: 0, missingDocs: 0 };
    }
    const datesSorted = items
      .map(s => s.buchungstag)
      .filter(Boolean)
      .sort();
    const fromDate = datesSorted[0];
    const toDate = datesSorted[datesSorted.length - 1];
    const fromLabel = fromDate
      ? new Date(fromDate).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' })
      : '';
    const toLabel = toDate
      ? new Date(toDate).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' })
      : '';
    const uncategorized = items.filter(s =>
      !(s.von_konto_id && s.nach_konto_id) && (!s.categories || s.categories.length === 0)
    ).length;
    const missingDocs = items.filter(s =>
      s.betrag < 0 && !(s.von_konto_id && s.nach_konto_id) && (!s.documents || s.documents.length === 0)
    ).length;
    return { count: items.length, fromLabel, toLabel, uncategorized, missingDocs };
  }, [filteredStatements]);

  const monthYear = new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  const selectedAccountName = useMemo(() => {
    const found = accounts.find(a => String(a.id) === String(selectedAccount));
    return found?.name ?? 'Alle Konten';
  }, [accounts, selectedAccount]);

  // ========================================================================
  // LOADING SKELETON (Story-Style)
  // ========================================================================
  if (isLoading) {
    return (
      <div
        className="min-h-screen font-sans text-gray-800 dark:text-slate-200"
        style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">
          <div className="pt-4 pb-6 animate-pulse">
            <div className="h-3 w-40 bg-slate-700/60 rounded mb-4" />
            <div className="h-12 w-72 bg-slate-700/60 rounded mb-3" />
            <div className="h-4 w-full max-w-md bg-slate-700/40 rounded" />
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div
                key={i}
                className="rounded-2xl p-4 animate-pulse bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-slate-700/60" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-3/4 bg-slate-700/60 rounded" />
                    <div className="h-3 w-1/2 bg-slate-700/40 rounded" />
                  </div>
                  <div className="h-6 w-24 bg-slate-700/60 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ========================================================================
  // ERROR STATE
  // ========================================================================
  if (error) {
    return (
      <div
        className="min-h-screen font-sans text-gray-800 dark:text-slate-200"
        style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10 pt-10">
          <div
            className="rounded-3xl p-10 text-center bg-white dark:bg-[#1c2432]"
            style={{ border: '1px solid rgba(244,63,94,0.30)'  }}
          >
            <div className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5 bg-rose-500/10 border border-rose-500/30">
              <ExclamationTriangleIcon className="h-10 w-10 text-rose-600 dark:text-rose-400" />
            </div>
            <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">Fehler aufgetreten</h2>
            <p className="text-gray-600 dark:text-slate-400 text-base mt-3">{error}</p>
            <button
              onClick={fetchData}
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-primary-500 text-slate-950 hover:bg-primary-400 font-bold text-sm transition"
            >
              Erneut versuchen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ========================================================================
  // MAIN RENDER
  // ========================================================================
  const hasStatements = allStatements.length > 0;
  const hasFilteredStatements = filteredStatements.length > 0;

  return (
    <div
      className="min-h-screen font-sans text-gray-800 dark:text-slate-200"
      style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">

        {/* ============================================================ */}
        {/* INTRO-SCHRIFTZUG · Saldo links + Prosa rechts                  */}
        {/* ============================================================ */}
        <section className="pt-2 pb-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-info-400/80 font-semibold mb-3">
            Kontoauszüge · Stand {monthYear}
          </div>
          <h2
            className="text-4xl md:text-6xl font-black tracking-tight text-gray-900 dark:text-white leading-[1.02]"
            style={{ letterSpacing: '-0.04em' }}
          >
            Buchungs-<br />
            <span className="text-info-600 dark:text-info-400">Auszug.</span>
          </h2>

          {hasStatements && storyMeta.count > 0 ? (
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-12 items-start">
              {/* Saldo (links, groß) */}
              <div className="lg:min-w-[280px]">
                <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 dark:text-slate-500 font-bold">
                  Saldo aktuell
                </div>
                <div className={`text-5xl md:text-7xl font-black tabular-nums leading-none mt-2 ${stats.balance >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
                  {(() => {
                    const sign = stats.balance >= 0 ? '+' : '−';
                    const [intPart, restPart] = formatCurrency(Math.abs(stats.balance)).split(',');
                    return (
                      <>
                        {sign}{intPart}
                        <span className={`text-3xl md:text-4xl ${stats.balance >= 0 ? 'text-emerald-700/70 dark:text-emerald-300/70' : 'text-rose-700/70 dark:text-rose-300/70'}`}>
                          ,{restPart}
                        </span>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Prosa (rechts) */}
              <p className="text-gray-600 dark:text-slate-400 text-base md:text-lg leading-relaxed">
                <span className="font-bold text-gray-900 dark:text-white tabular-nums">{storyMeta.count} {storyMeta.count === 1 ? 'Buchung' : 'Buchungen'}</span>
                {' '}·{' '}
                <span className="font-bold text-gray-900 dark:text-white">{selectedAccountName}</span>
                {storyMeta.fromLabel && storyMeta.toLabel && storyMeta.fromLabel !== storyMeta.toLabel && (
                  <>
                    {' '}vom{' '}
                    <span className="font-bold text-gray-900 dark:text-white">{storyMeta.fromLabel}</span>
                    {' '}bis{' '}
                    <span className="font-bold text-gray-900 dark:text-white">{storyMeta.toLabel}</span>
                  </>
                )}
                {storyMeta.fromLabel && storyMeta.toLabel && storyMeta.fromLabel === storyMeta.toLabel && (
                  <>
                    {' '}am{' '}
                    <span className="font-bold text-gray-900 dark:text-white">{storyMeta.fromLabel}</span>
                  </>
                )}
                .
                {stats.income > 0 && (
                  <>
                    {' '}
                    <span className="font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">{formatCurrency(stats.income)} Eingänge</span>
                    {' stehen '}
                    <span className="font-bold text-rose-700 dark:text-rose-300 tabular-nums">{formatCurrency(stats.expenses)} Ausgänge</span>
                    {' gegenüber.'}
                  </>
                )}
                {storyMeta.uncategorized > 0 && (
                  <>
                    {' '}
                    <span className="font-bold text-amber-700 dark:text-amber-300">{storyMeta.uncategorized} ohne Kategorie</span>
                  </>
                )}
                {storyMeta.missingDocs > 0 && (
                  <>
                    {storyMeta.uncategorized > 0 ? ' · ' : ' '}
                    <span className="font-bold text-rose-700 dark:text-rose-300">{storyMeta.missingDocs} ohne Beleg</span>
                  </>
                )}
                {(storyMeta.uncategorized > 0 || storyMeta.missingDocs > 0) && '.'}
              </p>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-slate-400 text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
              {!hasStatements ? (
                <>
                  Noch keine Bewegungen erfasst. Importiere deinen <span className="font-bold text-gray-900 dark:text-white">ersten CSV-Auszug</span> oder lege eine manuelle Buchung an.
                </>
              ) : (
                <>
                  Aktuell <span className="font-bold text-amber-700 dark:text-amber-300">keine Treffer</span> für diese Filter — passe Konto, Jahr oder Suchbegriff an.
                </>
              )}
            </p>
          )}
        </section>

        {/* ============================================================ */}
        {/* ACTION-BANNER · Wenn unkategorisierte Buchungen vorliegen      */}
        {/* ============================================================ */}
        {hasStatements && storyMeta.uncategorized > 0 && !selectionMode && (
          <section className="pb-6">
            <article
              className="rounded-3xl p-5 sm:p-6 bg-white dark:bg-[#1c2432]"
              style={{ border: '1px solid rgba(245,158,11,0.30)', boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
            >
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0 w-full">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.40)' }}
                  >
                    <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">Action erforderlich</div>
                    <h4 className="text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                      {storyMeta.uncategorized} {storyMeta.uncategorized === 1 ? 'Buchung wartet' : 'Buchungen warten'} auf Zuordnung
                    </h4>
                    <p className="text-gray-700 dark:text-slate-300 text-sm mt-2">
                      Weise Kategorien zu, um Auswertungen sauber zu halten. {storyMeta.missingDocs > 0 && (
                        <>
                          Zusätzlich fehlen{' '}
                          <span className="font-bold text-rose-700 dark:text-rose-300">{storyMeta.missingDocs} Belege</span>.
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  onClick={enterSelectionMode}
                  className="w-full sm:w-auto px-5 py-2.5 rounded-xl text-sm font-bold border bg-amber-500/10 border-amber-500/30 text-amber-200 hover:bg-amber-500/20 transition shrink-0"
                >
                  Jetzt erledigen
                </button>
              </div>
            </article>
          </section>
        )}

        {/* ============================================================ */}
        {/* TOOLBAR · Search + Actions                                     */}
        {/* ============================================================ */}
        {hasStatements && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-600 dark:text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Buchungen suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-11 pl-11 pr-4 rounded-xl text-sm font-medium text-gray-900 dark:text-white placeholder-slate-500 outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                style={{ colorScheme: 'dark' }}
              />
            </div>
            <button
              onClick={() => setShowImportModal(true)}
              className="hidden md:flex w-11 h-11 items-center justify-center rounded-xl text-gray-700 dark:text-slate-300 hover:text-white transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
              title="CSV Import"
            >
              <DocumentIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              className="hidden md:flex w-11 h-11 items-center justify-center rounded-xl text-gray-700 dark:text-slate-300 hover:text-white transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
              title="Excel Export"
            >
              <DocumentArrowDownIcon className="h-5 w-5" />
            </button>
            {selectionMode ? (
              <button
                onClick={exitSelectionMode}
                className="h-11 px-4 flex items-center gap-2 rounded-xl text-sm font-bold border bg-amber-500/10 border-amber-500/30 text-amber-200 hover:bg-amber-500/20 transition"
              >
                <XMarkIcon className="h-5 w-5" />
                <span className="hidden sm:inline">Abbrechen</span>
              </button>
            ) : (
              <>
                <button
                  onClick={enterSelectionMode}
                  className="h-11 px-3 sm:px-4 flex items-center gap-2 rounded-xl text-sm font-semibold text-gray-800 dark:text-slate-200 hover:text-white hover:bg-white/10 transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  <ListBulletIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">Auswählen</span>
                </button>
                <button
                  onClick={() => setShowAddBookingModal(true)}
                  className="h-11 px-4 flex items-center gap-2 rounded-xl text-sm font-bold transition bg-primary-500 text-slate-950 hover:bg-primary-400"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">Neue Buchung</span>
                </button>
              </>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* FILTER-STRIP · Story-Pill-Stil (Konten · Jahre · Status · Tags)*/}
        {/* ============================================================ */}
        {hasStatements && (
          <div
            className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 mb-5 backdrop-blur-md"
            style={{
              background: 'rgba(15,23,42,0.65)',
              borderBottom: '1px solid rgba(51,65,85,0.6)',
            }}
          >
            <div className="space-y-2">
              {/* Account row */}
              <div className="flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-hide">
                {accounts.map((account) => {
                  const active = selectedAccount === String(account.id);
                  return (
                    <button
                      key={account.id}
                      onClick={() => setSelectedAccount(String(account.id))}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition border ${
                        active
                          ? 'bg-primary-500/15 border-primary-500/40 text-primary-200'
                          : 'border-white/10 text-slate-300 hover:bg-white/5 hover:text-white'
                      }`}
                      style={!active ? { background: 'rgba(255,255,255,0.03)' } : undefined}
                    >
                      {account.id === 'all' ? 'Alle Konten' : account.name}
                    </button>
                  );
                })}
              </div>

              {/* Year + Status row */}
              <div className="flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-hide items-center">
                {availableYears.map((year) => {
                  const active = selectedYear === String(year);
                  return (
                    <button
                      key={year}
                      onClick={() => setSelectedYear(String(year))}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition border ${
                        active
                          ? 'bg-primary-500/15 border-primary-500/40 text-primary-200'
                          : 'border-white/10 text-slate-300 hover:bg-white/5 hover:text-white'
                      }`}
                      style={!active ? { background: 'rgba(255,255,255,0.03)' } : undefined}
                    >
                      {year === 'all' ? 'Alle Jahre' : year}
                    </button>
                  );
                })}

                <div className="w-px h-5 bg-white/10 flex-shrink-0 mx-1" />

                <button
                  onClick={() => setStatusFilter('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition border ${
                    statusFilter === 'all'
                      ? 'bg-primary-500/15 border-primary-500/40 text-primary-200'
                      : 'border-white/10 text-slate-300 hover:bg-white/5 hover:text-white'
                  }`}
                  style={statusFilter !== 'all' ? { background: 'rgba(255,255,255,0.03)' } : undefined}
                >
                  Alle
                </button>
                <button
                  onClick={() => setStatusFilter('red')}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition border flex items-center gap-1.5 ${
                    statusFilter === 'red'
                      ? 'bg-rose-500/15 border-rose-500/40 text-rose-200'
                      : 'border-white/10 text-slate-300 hover:bg-white/5 hover:text-white'
                  }`}
                  style={statusFilter !== 'red' ? { background: 'rgba(255,255,255,0.03)' } : undefined}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                  Fehlt
                </button>
                <button
                  onClick={() => setStatusFilter('blue')}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition border flex items-center gap-1.5 ${
                    statusFilter === 'blue'
                      ? 'bg-blue-500/15 border-blue-500/40 text-blue-200'
                      : 'border-white/10 text-slate-300 hover:bg-white/5 hover:text-white'
                  }`}
                  style={statusFilter !== 'blue' ? { background: 'rgba(255,255,255,0.03)' } : undefined}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                  Teil
                </button>
                <button
                  onClick={() => setStatusFilter('green')}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition border flex items-center gap-1.5 ${
                    statusFilter === 'green'
                      ? 'bg-primary-500/15 border-primary-500/40 text-primary-200'
                      : 'border-white/10 text-slate-300 hover:bg-white/5 hover:text-white'
                  }`}
                  style={statusFilter !== 'green' ? { background: 'rgba(255,255,255,0.03)' } : undefined}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                  OK
                </button>
              </div>

              {/* Tag row */}
              {availableBuchungTags.length > 0 && (
                <div className="flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-hide">
                  {availableBuchungTags.map(({ tag, count }) => {
                    const active = filterTags.includes(tag);
                    return (
                      <button
                        key={tag}
                        onClick={() =>
                          setFilterTags(prev =>
                            prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
                          )
                        }
                        className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition border flex items-center gap-1.5 ${
                          active
                            ? 'bg-primary-500/15 border-primary-500/40 text-primary-200'
                            : 'border-white/10 text-slate-300 hover:bg-white/5 hover:text-white'
                        }`}
                        style={!active ? { background: 'rgba(255,255,255,0.03)' } : undefined}
                      >
                        <TagIcon className="w-3 h-3" />
                        {tag} <span className="text-gray-500 dark:text-slate-500 tabular-nums">({count})</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* WELCOME SCREEN · Empty Initial State (no statements)           */}
        {/* ============================================================ */}
        {!hasStatements ? (
          <section className="pb-6">
            <div
              className="rounded-3xl p-8 sm:p-12 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
            >
              <div className="max-w-2xl mx-auto text-center">
                <div className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5 bg-primary-500/10 border border-primary-500/30">
                  <BanknotesIcon className="h-10 w-10 text-primary-600 dark:text-primary-400" />
                </div>
                <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">
                  Willkommen bei den Kontoauszügen
                </h2>
                <p className="text-gray-600 dark:text-slate-400 text-base mt-3 max-w-md mx-auto">
                  Verwalte alle Kontobewegungen übersichtlich — vom CSV-Import bis zum Excel-Export für den Steuerberater.
                </p>

                {/* 4 Schritte als Story-Tiles */}
                <div className="mt-8 space-y-3 text-left">
                  {[
                    { num: 1, icon: DocumentIcon, title: 'CSV-Dateien importieren', desc: 'Lade Kontoauszüge als CSV-Datei von deiner Bank herunter und importiere sie hier.', meta: 'Sparkasse · Commerzbank · DKB · weitere' },
                    { num: 2, icon: PlusIcon, title: 'Manuelle Buchungen erfassen', desc: 'Erfasse Bargeld-Transaktionen oder andere Buchungen manuell.', meta: 'Ein- und Auszahlungen · Umbuchungen · Kategorisierung' },
                    { num: 3, icon: TagIcon, title: 'Kategorien zuordnen', desc: 'Ordne Buchungen Kategorien zu für eine bessere Übersicht.', meta: 'KI kann Kategorien automatisch vorschlagen' },
                    { num: 4, icon: DocumentArrowDownIcon, title: 'Excel-Export', desc: 'Exportiere als Excel-Datei für Analysen oder die Steuererklärung.', meta: 'Filter nach Jahr & Konto · inkl. Kategorien & Notizen' },
                  ].map(({ num, icon: Icon, title, desc, meta }) => (
                    <div
                      key={num}
                      className="flex gap-4 px-4 py-4 rounded-2xl"
                      style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.5)' }}
                    >
                      <div
                        className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm"
                        style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }}
                      >
                        {num}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 dark:text-white text-sm flex items-center gap-2 mb-1">
                          <Icon className="h-4 w-4 text-primary-700 dark:text-primary-300" />
                          {title}
                        </p>
                        <p className="text-gray-600 dark:text-slate-400 text-xs leading-relaxed">{desc}</p>
                        <p className="text-gray-500 dark:text-slate-500 text-[11px] mt-1">{meta}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary-500 text-slate-950 hover:bg-primary-400 font-bold text-sm transition"
                  >
                    <DocumentIcon className="h-5 w-5" />
                    CSV-Datei importieren
                  </button>
                  <button
                    onClick={() => setShowAddBookingModal(true)}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-gray-800 dark:text-slate-200 hover:text-white text-sm font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    <PlusIcon className="h-5 w-5" />
                    Erste Buchung erfassen
                  </button>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <>
            {/* ============================================================ */}
            {/* SUCHERGEBNIS-ZUSAMMENFASSUNG                                   */}
            {/* ============================================================ */}
            {searchTerm && hasFilteredStatements && (() => {
              const searchEinnahmen = filteredStatements.filter(s => s.betrag > 0 && s.originalId !== 'start').reduce((sum, s) => sum + s.betrag, 0);
              const searchAusgaben = filteredStatements.filter(s => s.betrag < 0 && s.originalId !== 'start').reduce((sum, s) => sum + Math.abs(s.betrag), 0);
              const searchSumme = filteredStatements.filter(s => s.originalId !== 'start').reduce((sum, s) => sum + s.betrag, 0);
              return (
                <div
                  className="rounded-2xl p-3 sm:p-4 mb-5 flex flex-wrap items-center gap-3 sm:gap-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                >
                  <div className="text-sm">
                    <span className="font-black text-gray-900 dark:text-white tabular-nums">{filteredStatements.length}</span>
                    <span className="text-gray-600 dark:text-slate-400 ml-1">Treffer</span>
                  </div>
                  <div className="hidden sm:block w-px h-5 bg-white/10" />
                  <div className="text-sm">
                    <span className="text-gray-600 dark:text-slate-400">Eingänge </span>
                    <span className="font-bold text-primary-700 dark:text-primary-300 tabular-nums">{formatCurrency(searchEinnahmen)}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-600 dark:text-slate-400">Ausgaben </span>
                    <span className="font-bold text-rose-700 dark:text-rose-300 tabular-nums">{formatCurrency(searchAusgaben)}</span>
                  </div>
                  <div className="hidden sm:block w-px h-5 bg-white/10" />
                  <div className="text-sm">
                    <span className="text-gray-600 dark:text-slate-400">Summe </span>
                    <span className={`font-black tabular-nums ${searchSumme >= 0 ? 'text-primary-300' : 'text-rose-300'}`}>{formatCurrency(searchSumme)}</span>
                  </div>
                </div>
              );
            })()}

            {/* ============================================================ */}
            {/* BUCHUNGS-LISTE · Story-Rows mit Date-Tile                      */}
            {/* ============================================================ */}
            <div className="space-y-6">
              {hasFilteredStatements ? (
                groupedStatements.map((group, groupIndex) => {
                  const dayIncome = group.statements
                    .filter(s => s.betrag > 0 && s.originalId !== 'start' && !(selectedAccount === 'all' && s.von_konto_id && s.nach_konto_id))
                    .reduce((sum, s) => sum + s.betrag, 0);
                  const dayExpenses = group.statements
                    .filter(s => s.betrag < 0 && s.originalId !== 'start' && !(selectedAccount === 'all' && s.von_konto_id && s.nach_konto_id))
                    .reduce((sum, s) => sum + Math.abs(s.betrag), 0);
                  const dayNet = dayIncome - dayExpenses;
                  const dayCount = group.statements.filter(s => s.originalId !== 'start').length;

                  // Year divider
                  const currentYear = new Date(group.date).getFullYear();
                  const prevYear = groupIndex > 0 ? new Date(groupedStatements[groupIndex - 1].date).getFullYear() : currentYear;
                  const showYearDivider = groupIndex > 0 && currentYear !== prevYear;

                  return (
                    <div key={group.date}>
                      {/* Year-Divider */}
                      {showYearDivider && (() => {
                        const lastStmtOfYear = filteredStatements.find(s =>
                          s.saldo != null && new Date(s.buchungstag).getFullYear() === currentYear
                        );
                        const yearEndSaldo = lastStmtOfYear?.saldo ?? 0;

                        return (
                          <div className="flex items-center gap-4 mb-6 mt-2">
                            <div className="flex-1 h-px bg-white/10" />
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700" >
                              <span className="text-[10px] uppercase tracking-widest font-bold text-gray-600 dark:text-slate-400">Jahresabschluss {currentYear}</span>
                              <span className={`text-xs font-black tabular-nums ${yearEndSaldo >= 0 ? 'text-primary-300' : 'text-rose-300'}`}>
                                {formatCurrency(yearEndSaldo)}
                              </span>
                            </div>
                            <div className="flex-1 h-px bg-white/10" />
                          </div>
                        );
                      })()}

                      {/* Date Group Header (sticky strip-style) */}
                      <div
                        className="flex items-center justify-between mb-3 px-4 sm:px-5 py-2.5 rounded-2xl"
                        style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.5)' }}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/[0.08]"
                          >
                            <span className="text-xs font-black text-gray-800 dark:text-slate-200 tabular-nums">{getDayNumber(group.date)}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="hidden sm:block text-sm font-bold text-gray-900 dark:text-white truncate">{formatDateFull(group.date)}</p>
                            <p className="sm:hidden text-xs font-bold text-gray-900 dark:text-white truncate">{formatDateCompact(group.date)}</p>
                            <p className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mt-0.5">
                              {dayCount} {dayCount === 1 ? 'Transaktion' : 'Transaktionen'}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`px-2.5 py-1 rounded-full font-bold text-[11px] tabular-nums shrink-0 ${
                            dayNet >= 0
                              ? 'bg-primary-500/10 text-primary-300 border border-primary-500/30'
                              : 'bg-rose-500/10 text-rose-300 border border-rose-500/30'
                          }`}
                        >
                          {dayNet >= 0 ? '+' : ''}{formatCurrency(dayNet)}
                        </span>
                      </div>

                      {/* Story-Rows Card */}
                      <div
                        className="rounded-2xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                        style={{ boxShadow: '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 30px 60px -20px rgba(0,0,0,0.55)' }}
                      >
                        {group.statements.map((statement, idx) => {
                          const status = getStatusInfo(statement);
                          const isNormalBooking = statement.originalId !== 'start' && !(statement.von_konto_id && statement.nach_konto_id);
                          const hasCategories = statement.categories && statement.categories.length > 0;
                          const isSelectable = isNormalBooking && !hasCategories;
                          const isSelected = selectedIds.has(statement.originalId);
                          const isPositive = statement.betrag >= 0;

                          // Date-Tile Tone
                          let tileBg: string;
                          let tileBorder: string;
                          let tileDayColor: string;
                          if (statement.originalId === 'start' || (statement.von_konto_id && statement.nach_konto_id)) {
                            tileBg = 'rgba(255,255,255,0.03)';
                            tileBorder = 'rgba(255,255,255,0.08)';
                            tileDayColor = 'text-slate-400';
                          } else if (isPositive) {
                            tileBg = 'linear-gradient(180deg, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.04) 100%)';
                            tileBorder = 'rgba(16,185,129,0.30)';
                            tileDayColor = 'text-primary-300';
                          } else {
                            tileBg = 'linear-gradient(180deg, rgba(244,63,94,0.16) 0%, rgba(244,63,94,0.04) 100%)';
                            tileBorder = 'rgba(244,63,94,0.30)';
                            tileDayColor = 'text-rose-300';
                          }

                          const accountName = accounts.find(a => a.id === statement.konto_id)?.name;
                          const isLastInGroup = idx === group.statements.length - 1;

                          return (
                            <div
                              key={statement.id}
                              onClick={() => {
                                if (selectionMode && isSelectable) toggleSelection(statement.originalId);
                                else if (!selectionMode) setSelectedStatementId(statement.originalId);
                              }}
                              className={`flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 transition cursor-pointer ${
                                selectionMode && !isSelectable ? 'opacity-40 cursor-not-allowed' : ''
                              }`}
                              style={{
                                borderBottom: isLastInGroup ? 'none' : '1px solid #1e293b',
                                background: selectionMode && isSelected ? 'rgba(16,185,129,0.08)' : 'transparent',
                              }}
                              onMouseEnter={(e) => {
                                if (!(selectionMode && isSelected) && !(selectionMode && !isSelectable)) {
                                  e.currentTarget.style.background = 'rgba(255,255,255,0.025)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!(selectionMode && isSelected) && !(selectionMode && !isSelectable)) {
                                  e.currentTarget.style.background = 'transparent';
                                }
                              }}
                            >
                              {/* Selection checkbox */}
                              {selectionMode && isSelectable && (
                                <div
                                  className={`shrink-0 w-5 h-5 rounded-md flex items-center justify-center transition ${
                                    isSelected ? 'bg-primary-500 border-primary-500' : ''
                                  }`}
                                  style={!isSelected ? { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.20)' } : undefined}
                                >
                                  {isSelected && <CheckIcon className="h-3 w-3 text-slate-950" />}
                                </div>
                              )}

                              {/* Date-Tile (color-coded) */}
                              <div
                                className="shrink-0 flex flex-col items-center justify-center"
                                style={{
                                  width: 56,
                                  height: 68,
                                  borderRadius: 14,
                                  background: tileBg,
                                  border: `1px solid ${tileBorder}`,
                                }}
                              >
                                <div className={`text-[9px] uppercase tracking-widest font-bold ${tileDayColor}`}>
                                  {getDayShort(statement.buchungstag)}
                                </div>
                                <div className="text-xl font-black text-gray-900 dark:text-white leading-none mt-0.5 tabular-nums">
                                  {getDayNumber(statement.buchungstag)}
                                </div>
                                <div className="text-[9px] text-gray-600 dark:text-slate-400 mt-0.5">
                                  {getMonthShort(statement.buchungstag)}
                                </div>
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate">
                                    {statement.name_zahlungsbeteiligter || statement.buchungstext}
                                  </p>
                                  {statement.von_konto_id && statement.nach_konto_id && (
                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30">
                                      <ArrowsRightLeftIcon className="w-2.5 h-2.5" />
                                      Umbuchung
                                    </span>
                                  )}
                                  {status.color === 'green' && (
                                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary-500/15">
                                      <CheckIcon className="w-2.5 h-2.5 text-primary-600 dark:text-primary-400" />
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-600 dark:text-slate-400 truncate mt-0.5">
                                  {statement.verwendungszweck || 'Kein Verwendungszweck'}
                                  {accountName && (
                                    <>
                                      <span className="text-slate-600"> · </span>
                                      <span className="text-gray-500 dark:text-slate-500">{accountName}</span>
                                    </>
                                  )}
                                </p>
                                <div className="flex items-center gap-1 flex-wrap">
                                  {renderStatusBadges(statement)}
                                  {statement.tags && statement.tags.length > 0 && statement.tags.map(tag => (
                                    <span key={tag} className="text-[10px] font-semibold text-primary-700 dark:text-primary-300 bg-primary-500/10 border border-primary-500/30 px-1.5 py-0.5 rounded-full mt-1">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              {/* Amount + Saldo */}
                              <div className="shrink-0 text-right">
                                <p className={`text-sm sm:text-base font-black tabular-nums ${isPositive ? 'text-primary-300' : 'text-rose-300'}`}>
                                  {isPositive ? '+' : ''}{formatCurrency(statement.betrag)}
                                </p>
                                <p className="text-[11px] text-gray-500 dark:text-slate-500 tabular-nums mt-0.5">
                                  Saldo {formatCurrency(getSaldo(statement))}
                                </p>
                              </div>

                              {/* Chevron */}
                              {!selectionMode && (
                                <ChevronRightIconSolid className="hidden sm:block w-4 h-4 text-gray-500 dark:text-slate-500 shrink-0" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              ) : (
                /* Empty Filter State */
                <div
                  className="rounded-3xl p-10 sm:p-14 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                  style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
                >
                  <div
                    className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5"
                    style={{ background: 'rgba(148,163,184,0.10)', border: '1px solid rgba(148,163,184,0.30)' }}
                  >
                    <DocumentTextIcon className="h-10 w-10 text-gray-600 dark:text-slate-400" />
                  </div>
                  <h4 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">Keine Treffer</h4>
                  <p className="text-gray-600 dark:text-slate-400 text-base mt-3 max-w-md mx-auto">
                    Mit den aktuellen Filtern findet sich keine Buchung. Probier es mit einem anderen Konto, Jahr oder Suchbegriff.
                  </p>
                  <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedAccount('all');
                        setSelectedYear('all');
                        setStatusFilter('all');
                        setFilterTags([]);
                      }}
                      className="px-5 py-3 rounded-xl text-gray-800 dark:text-slate-200 hover:text-white text-sm font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                    >
                      Filter zurücksetzen
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ============================================================ */}
        {/* PAGINATION                                                     */}
        {/* ============================================================ */}
        {totalPages > 1 && (
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 order-2 sm:order-1 tabular-nums">
              <span className="text-gray-900 dark:text-white font-bold">{startIndex + 1}-{Math.min(startIndex + statementsPerPage, filteredStatements.length)}</span>
              <span className="ml-1">von {filteredStatements.length}</span>
            </div>

            <div className="flex items-center gap-1 sm:gap-2 order-1 sm:order-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 ${`p-2 rounded-lg transition ${
                  currentPage === 1
                    ? 'opacity-40 cursor-not-allowed text-slate-500'
                    : 'text-slate-300 hover:text-white hover:bg-white/10'
                }`}`}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = currentPage - 2 + i;
                if (pageNum < 1 || pageNum > totalPages) return null;
                const active = currentPage === pageNum;
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`min-w-[36px] h-9 px-2 rounded-lg text-sm font-bold transition tabular-nums ${
                      active
                        ? 'bg-primary-500 text-slate-950'
                        : 'text-slate-300 hover:text-white hover:bg-white/10'
                    }`}
                    style={!active ? { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' } : undefined}
                  >
                    {pageNum}
                  </button>
                );
              }).filter(Boolean)}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 ${`p-2 rounded-lg transition ${
                  currentPage === totalPages
                    ? 'opacity-40 cursor-not-allowed text-slate-500'
                    : 'text-slate-300 hover:text-white hover:bg-white/10'
                }`}`}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* BULK SELECTION ACTION-BAR · Sticky bottom (Story-Style)        */}
        {/* ============================================================ */}
        {selectionMode && selectedIds.size > 0 && (
          <div className="fixed bottom-0 left-0 right-0 z-40 sm:sticky sm:bottom-4 sm:mt-4 px-4 sm:px-0">
            <div
              className="max-w-7xl mx-auto rounded-t-3xl sm:rounded-2xl px-5 py-3.5 backdrop-blur-md"
              style={{
                background: 'rgba(2,6,23,0.85)',
                border: '1px solid rgba(16,185,129,0.30)',
                boxShadow: '0 30px 60px -10px rgba(0,0,0,0.7), 0 0 0 1px rgba(16,185,129,0.2)',
              }}
            >
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex items-center gap-3 shrink-0">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center font-black"
                    style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.30)', color: '#86efac' }}
                  >
                    <span className="tabular-nums text-sm">{selectedIds.size}</span>
                  </div>
                  <div className="text-sm">
                    <div className="font-bold text-gray-900 dark:text-white">ausgewählt</div>
                    <div className="text-gray-600 dark:text-slate-400 tabular-nums">{formatCurrency(selectedSum)}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-1 sm:justify-end flex-wrap">
                  <button
                    onClick={selectAllVisible}
                    className="px-3 py-2 rounded-lg text-xs font-bold text-gray-800 dark:text-slate-200 hover:text-white transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    Alle
                  </button>
                  <button
                    onClick={selectUncategorized}
                    className="px-3 py-2 rounded-lg text-xs font-bold text-gray-800 dark:text-slate-200 hover:text-white transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    Ohne Kategorie
                  </button>
                  <button
                    onClick={() => setSelectedIds(new Set())}
                    className="px-3 py-2 rounded-lg text-xs font-bold text-gray-800 dark:text-slate-200 hover:text-white transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    Keine
                  </button>

                  <div className="relative">
                    <button
                      onClick={() => setShowBulkCategoryDropdown(!showBulkCategoryDropdown)}
                      disabled={bulkAssigning}
                      className="px-4 py-2 rounded-xl text-sm font-bold transition bg-primary-500 text-slate-950 hover:bg-primary-400 disabled:opacity-50 flex items-center gap-2"
                    >
                      <TagIcon className="h-4 w-4" />
                      {bulkAssigning ? 'Wird zugewiesen...' : 'Kategorie zuweisen'}
                    </button>

                    {/* Category Dropdown (Story-Stil) */}
                    {showBulkCategoryDropdown && (
                      <div
                        className="absolute bottom-full mb-2 right-0 w-72 max-h-64 overflow-y-auto rounded-2xl z-50 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                        style={{ boxShadow: '0 30px 60px -10px rgba(0,0,0,0.7)' }}
                      >
                        <div ref={bulkCategoryRef}>
                          {sortedBulkCategories.map((cat: any) => (
                            <button
                              key={cat.id}
                              onClick={() => handleBulkCategoryAssign(cat.id)}
                              className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-800 dark:text-slate-200 hover:bg-white/5 hover:text-white transition"
                              style={{ borderBottom: '1px solid #1e293b' }}
                            >
                              {cat.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* MODALS · Reuse existing modal components verbatim              */}
        {/* ============================================================ */}
        {selectedStatementId && (
          <StatementDetailModal
            statementId={selectedStatementId}
            onClose={() => setSelectedStatementId(null)}
            onStatementUpdate={handleStatementUpdate}
            onStatementDelete={handleStatementDelete}
          />
        )}

        <ExcelExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          accounts={accounts}
          allStatements={allStatements}
          filteredStatements={filteredStatements}
          hasActiveFilters={!!(searchTerm || selectedAccount !== 'all' || selectedYear !== 'all' || statusFilter !== 'all')}
          isMobile={window.innerWidth < 640}
        />

        {showImportModal && (
          <CSVImporter
            onImportSuccess={() => {
              setShowImportModal(false);
              fetchData();
            }}
            onClose={() => setShowImportModal(false)}
          />
        )}

        <AddBookingModal
          isOpen={showAddBookingModal}
          onClose={() => setShowAddBookingModal(false)}
          onSuccess={() => {
            fetchData();
          }}
        />

        <ConfirmationModal
          visible={confirmModal.visible}
          onClose={() => setConfirmModal({ ...confirmModal, visible: false })}
          type={confirmModal.type}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
          button={confirmModal.variant === 'single' ? {
            text: 'OK',
            onPress: () => {
              confirmModal.onConfirm();
              setConfirmModal({ ...confirmModal, visible: false });
            }
          } : undefined}
          buttons={confirmModal.variant === 'two' ? {
            cancel: {
              text: 'Abbrechen',
              onPress: () => setConfirmModal({ ...confirmModal, visible: false })
            },
            confirm: {
              text: 'Bestätigen',
              onPress: () => {
                confirmModal.onConfirm();
                setConfirmModal({ ...confirmModal, visible: false });
              }
            }
          } : undefined}
        />
      </div>
    </div>
  );
};

export default AccountStatements;
